'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';
import { FitventMark } from '@/components/design/FitventMark';

/**
 * The page shown when something breaks or isn't there, in the v8 look: the
 * app's own canvas and ink (light or dark with the theme), sentence case, and
 * one way on. Nothing else: inside the app that way is back to the dashboard
 * or myfitvent, and a list of other places only made people choose.
 *
 * Two placements:
 *   - full page (the root 404 and error, and any dashboard or hub page that
 *     renders without a rail): the lockup top left, the message centred;
 *   - in a shell (`inShell`): only the message, sitting in the dashboard's or
 *     the hub's content column, so the rail and its navigation stay.
 */

export function FvdErrorScreen({
  title,
  body,
  primary,
  footnote,
  lockup = 'fitvent',
  inShell = false,
  gutter = 16,
}: {
  title: string;
  body: string;
  /** The one action. */
  primary: ReactNode;
  footnote?: ReactNode;
  /** Full page only: which lockup sits top left. */
  lockup?: 'fitvent' | 'myfitvent';
  /** Render inside the dashboard or hub shell rather than as a page of its own. */
  inShell?: boolean;
  /** In a shell: the side padding. The hub's content column already has its own, the dashboard's has none. */
  gutter?: number;
}) {
  const message = (
    <div style={{ maxWidth: 440, width: '100%' }}>
      <FvdErrorMark size={inShell ? 68 : 84} />
      <h1 style={{ margin: inShell ? '22px 0 0' : '26px 0 0', fontSize: 24, fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.25 }}>{title}</h1>
      <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fvd-ink-secondary)' }}>{body}</p>
      <div style={{ display: 'flex', marginTop: 24 }}>{primary}</div>
      {footnote && (
        <p style={{ margin: '24px 0 0', fontSize: 12, color: 'var(--fvd-ink-muted)', fontFamily: 'var(--fvd-font-mono)', overflowWrap: 'anywhere' }}>{footnote}</p>
      )}
    </div>
  );

  if (inShell) {
    return (
      <div
        style={{
          minHeight: '62vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `40px ${gutter}px 64px`,
          boxSizing: 'border-box',
          color: 'var(--fvd-ink)',
          fontFamily: 'var(--fvd-font-body)',
        }}
      >
        {message}
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-body)',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        boxSizing: 'border-box',
      }}
    >
      {/* The sign-in pages' header: the same lockup size and the same edges,
          so a missing page reads as part of the same front door. */}
      <div className="px-4 py-4 min-[600px]:px-7 min-[600px]:py-5" style={{ display: 'flex', alignItems: 'center' }}>
        {/* As tall as the sign-in header's switch button (44, then 36 from 768), so the lockup lands at the same height. */}
        <span className="h-[44px] min-[768px]:h-[36px]" style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
          <FitventMark size={20} />
          <span className="fvd-wordmark" style={{ fontSize: 17 }}>
            {lockup === 'myfitvent' && <span className="fvd-wordmark-my">my</span>}fitvent
          </span>
        </span>
      </div>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        {message}
      </main>
    </div>
  );
}

/**
 * The Fitvent mark as a small drawing, with its last bar knocked loose. One
 * drawing for a missing page and a failed one alike. The geometry and the two
 * brand fills are the mark's own (components/design/FitventMark.tsx).
 * Decorative, so hidden from screen readers: the heading says what happened.
 */
export function FvdErrorMark({ size = 76 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: 'none', overflow: 'visible' }}
    >
      <polygon points="8,14 92,14 92,28 22,28" fill="var(--fvd-mark-accent)" />
      <polygon points="34,44 92,44 92,58 48,58" fill="var(--fvd-mark-ink)" />
      <polygon points="68,74 92,74 92,88 78,88" fill="var(--fvd-mark-ink)" transform="translate(-6 7) rotate(16 80 81)" />
    </svg>
  );
}

const base = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  padding: '0 18px',
  borderRadius: 'var(--fvd-radius)',
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  whiteSpace: 'nowrap' as const,
};

/**
 * The one primary, in the v8 teal: a link, or (`onClick`) a button.
 *
 * `fullLoad` makes the link a plain anchor, a whole page load. The error views
 * use it: a client-side link to the page that just failed (an error on the
 * dashboard's own front page, say) would not leave it, and the reload also
 * fetches everything again, which is what recovers a server error.
 */
export function ErrorPrimary({
  href,
  onClick,
  fullLoad = false,
  children,
}: {
  href?: string;
  onClick?: () => void;
  fullLoad?: boolean;
  children: ReactNode;
}) {
  // The solid teal, as on every other large button (FvdButton, sign in): the
  // plain accent is the bright one in dark, which glares across a button.
  const style = { ...base, border: 'none', background: 'var(--fvd-teal-solid)', color: 'var(--fvd-teal-solid-on)' };
  if (!href) {
    return <button type="button" className="fvd-hoverable-solid" onClick={onClick} style={{ ...style, fontFamily: 'inherit', cursor: 'pointer' }}>{children}</button>;
  }
  if (fullLoad) {
    // A plain anchor on purpose: a whole page load, see above.
    return <a href={href} className="fvd-hoverable-solid" style={style}>{children}</a>;
  }
  return <Link href={href} className="fvd-hoverable-solid" style={style}>{children}</Link>;
}
